/**
 * Style quiz — Figma page Quizzes, `Style quiz · desktop` 3185:16166 / `mobile` 3183:17532.
 *
 * Order: Welcome → Gender (only if Personal data has none) → Brands → Heels (women)
 * → Colors → Styles (≥ 2) → Rate intro → Rate outfits → Result. Answers are saved at
 * every step (R203), so leaving keeps progress.
 */
import type { HeelFrequency, HeelHeight } from '@/types/my-style'
import type { TasteMatchingSwipeScore } from '@/features/taste/types/taste-config'

export type StyleQuizStep =
  | 'welcome'
  | 'gender'
  | 'brands'
  | 'heels'
  | 'colors'
  | 'styles'
  | 'rate-intro'
  | 'rate'
  | 'result'

export type StyleQuizGender = 'woman' | 'man'

/** Figma Heels: Never / Rarely / Sometimes / Often. */
export const STYLE_QUIZ_HEEL_FREQUENCIES = ['never', 'rarely', 'sometimes', 'often'] as const
export type StyleQuizHeelFrequency = (typeof STYLE_QUIZ_HEEL_FREQUENCIES)[number]

/** Figma Heels: Up to 4 cm / 5–7 cm / 8+ cm (R94: hidden for Never). */
export const STYLE_QUIZ_HEEL_HEIGHTS = ['low', 'mid', 'high'] as const
export type StyleQuizHeelHeight = (typeof STYLE_QUIZ_HEEL_HEIGHTS)[number]

export const STYLE_QUIZ_HEEL_HEIGHT_API: Record<StyleQuizHeelHeight, HeelHeight> = {
  low: 'up-to-4cm',
  mid: '5-7cm',
  high: '8cm-plus',
}

/** Continue on Styles is active from 2 selected (06 / 07). */
export const STYLE_QUIZ_MIN_STYLES = 2

/** Rate intro: "5 outfits" per style. */
export const STYLE_QUIZ_OUTFITS_PER_STYLE = 5

/** Steps that ask something — the progress bar runs over these. */
const QUESTION_STEPS: readonly StyleQuizStep[] = ['gender', 'brands', 'heels', 'colors', 'styles', 'rate-intro']

export interface StyleQuizFlowInput {
  /** Personal data had no gender when the quiz opened. */
  askGender: boolean
  gender: StyleQuizGender | null
}

export function styleQuizSteps({ askGender, gender }: StyleQuizFlowInput): StyleQuizStep[] {
  const steps: StyleQuizStep[] = ['welcome']
  if (askGender) steps.push('gender')
  steps.push('brands')
  // Women only; an unknown gender keeps the step (it is asked first in that case).
  if (gender !== 'man') steps.push('heels')
  steps.push('colors', 'styles', 'rate-intro', 'rate', 'result')
  return steps
}

export function nextStyleQuizStep(steps: StyleQuizStep[], current: StyleQuizStep): StyleQuizStep {
  const index = steps.indexOf(current)
  return steps[Math.min(index + 1, steps.length - 1)] ?? current
}

export function previousStyleQuizStep(steps: StyleQuizStep[], current: StyleQuizStep): StyleQuizStep {
  const index = steps.indexOf(current)
  return steps[Math.max(index - 1, 0)] ?? current
}

/** 0…1. Welcome is empty, Result full; Rate counts the outfits rated. */
export function styleQuizProgress(
  steps: StyleQuizStep[],
  current: StyleQuizStep,
  rated = 0,
  total = 0,
): number {
  if (current === 'welcome') return 0
  if (current === 'result') return 1
  if (current === 'rate') return total ? rated / total : 0
  const questions = steps.filter((step) => QUESTION_STEPS.includes(step))
  const index = questions.indexOf(current)
  return index < 0 ? 0 : (index + 1) / (questions.length + 1)
}

export type StyleQuizHeaderRight = 'none' | 'skip' | 'counter'

/** Quiz/Header right slot per step (Brands, Heels, Colors — Skip; Rate — Counter). */
export function styleQuizHeaderRight(step: StyleQuizStep): StyleQuizHeaderRight {
  if (step === 'brands' || step === 'heels' || step === 'colors') return 'skip'
  if (step === 'rate') return 'counter'
  return 'none'
}

/**
 * The API has no Rarely yet (BE-151) — it is sent as Sometimes and kept locally so the
 * quiz and its summary show what the user chose.
 */
export function toStyleQuizApiHeelFrequency(value: StyleQuizHeelFrequency): HeelFrequency {
  return value === 'rarely' ? 'sometimes' : value
}

export function fromStyleQuizApiHeelHeight(value: string | null | undefined): StyleQuizHeelHeight | null {
  if (!value) return null
  const match = STYLE_QUIZ_HEEL_HEIGHTS.find(
    (code) => STYLE_QUIZ_HEEL_HEIGHT_API[code] === value || code === value,
  )
  return match ?? null
}

export function resolveStyleQuizGender(value: string | null | undefined): StyleQuizGender | null {
  const key = value?.toLowerCase()
  if (key === 'woman' || key === 'female') return 'woman'
  if (key === 'man' || key === 'male') return 'man'
  return null
}

/** Rating 0–100 → the API's score 1..5 and the action it must agree with (§ 3.2). */
export function styleQuizSwipe(value: number): {
  score: TasteMatchingSwipeScore
  action: 'like' | 'dislike' | 'skip'
} {
  const score = (Math.round(Math.max(0, Math.min(100, value)) / 25) + 1) as TasteMatchingSwipeScore
  const action = score >= 4 ? 'like' : score <= 2 ? 'dislike' : 'skip'
  return { score, action }
}

/** Ids to add and to remove so the saved set becomes `next`. */
export function diffStyleQuizIds(saved: number[], next: number[]): { add: number[]; remove: number[] } {
  const before = new Set(saved)
  const after = new Set(next)
  return {
    add: next.filter((id) => !before.has(id)),
    remove: saved.filter((id) => !after.has(id)),
  }
}

/** Result row: up to three names, then "+N". */
export function styleQuizSummaryList(names: string[], limit = 3): string {
  if (!names.length) return ''
  const shown = names.slice(0, limit).join(', ')
  return names.length > limit ? `${shown} +${names.length - limit}` : shown
}
